<script setup>
import fallbackImage from '../assets/images/fallbackImage.png'

const props = defineProps({
  collection: {
    required: true,
  },
})
const { t } = useI18n()
const api = useApi()
const instructors = ref([])

async function fetchInstructors() {
  const instructorsData = await api.listInstructors({
    queryParameters: {
      content_collection: props.collection.id,
      fields: ['id', 'name', 'picture_url'],
    },
  })
  instructors.value = instructorsData?.results.map(person => ({
    id: person.id,
    name: person.name,
    imageUrl: person.picture_url,
  }))
}

const description = computed(() => {
  const details = props.collection?.description
  const ellipsis = '\u2026'
  if (details.length > 250)
    return `${details.substring(0, 250)}${ellipsis}`

  return details
})

const cardUrl = computed(() => {
  return props.collection ? `/on_demand/collection/${props.collection.id}` : null
})

const isPurchaseRequired = toRef(
  () => !props.collection?.active_customer_has_access,
)

const cardImage = computed(() => {
  if (props.collection?.thumbnail?.url) {
    return {
      src: props.collection?.thumbnail?.url,
      width: props.collection.thumbnail?.original_width || 400,
      height: props.collection.thumbnail?.original_height || 225,
    }
  }
  else if (props.collection?.content_thumbnail?.url) {
    return {
      src: props.collection?.content_thumbnail?.url,
      width: props.collection.content_thumbnail?.original_width || 400,
      height: props.collection.content_thumbnail?.original_height || 225,
    }
  }
  return {
    src: fallbackImage,
    width: 400,
    height: 225,
  }
})

onMounted(() => {
  fetchInstructors()
})
</script>

<template>
  <OnDemandCardTemplate>
    <BaseLink
      :url="cardUrl"
      class="flex flex-col gap-4"
    >
      <header class="relative">
        <div
          class="img-cover absolute inset-0 z-1 rounded-16px"
        />
        <DSImage
          v-if="cardImage"
          v-bind="cardImage"
          :alt="t('Content Thumbnail')"
          class="relative z-0 h-[225px] w-100% rounded-16px object-cover"
        />
        <div
          v-if="isPurchaseRequired"
          class="right-4 z-2"
          :class="!cardImage ? 'relative' : 'absolute bottom-4'"
        >
          <DSChip type="warning">
            {{ t('Purchase Required') }}
          </DSChip>
        </div>
      </header>
      <p class="flex justify-between gap-2">
        <strong class="text-text-heading fw-600">{{ collection?.name }}</strong>
        <DSAvatarList
          v-if="instructors?.length > 0"
          :people="instructors"
          class="!p-0"
          size="sm"
          :max="2"
          border
        />
      </p>
      <p class="text-14px text-secondary-300">
        <DSLineClamp
          :max-lines="3"
          :interactive="true"
        >
          <span>
            {{ description }}
          </span>
        </DSLineClamp>
      </p>

      <footer
        class="flex items-center gap-2"
      >
        <DSIcon
          icon="i-far-films"
          size="18"
        />
        <span>{{ collection?.content_count }}
          {{ t('Videos', collection?.content_count) }}</span>
      </footer>
    </BaseLink>
  </OnDemandCardTemplate>
</template>

<i18n>
{
  "en-us": {
    "Videos": "Videos | Video | Videos",
    "Purchase Required": "Purchase Required",
    "Content Thumbnail": "Content Thumbnail"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Content Thumbnail": "Vorschaubild für Inhalt",
  "Purchase Required": "Kauf erforderlich",
  "Videos": "Videos | Video | Videos"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Content Thumbnail": "Miniatura de contenido",
  "Purchase Required": "Compra necesaria",
  "Videos": "Vídeos | Vídeo | Vídeos"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Content Thumbnail": "Contenu de la vignette",
  "Purchase Required": "Achat requis",
  "Videos": "Vidéos | Vidéo | Vidéos"
}
</i18n>
